import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Pager, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

export default function Inventory() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adj, setAdj] = useState({ type: "restock", qty: 10, note: "" });

  const { data } = useQuery({
    queryKey: ["inventory", page, search, lowOnly],
    queryFn: async () => (await api.get(`/admin/inventory?page=${page}&limit=20&search=${encodeURIComponent(search)}&low_stock=${lowOnly}`)).data,
  });
  const { data: txns } = useQuery({
    queryKey: ["inv-txns"],
    queryFn: async () => (await api.get("/admin/inventory/transactions?limit=40")).data,
  });

  const adjust = useMutation({
    mutationFn: async () => api.post("/admin/inventory/adjust", { product_id: adjustFor.id, ...adj, qty: Number(adj.qty) }),
    onSuccess: () => { toast.success("Inventory updated"); setAdjustFor(null); qc.invalidateQueries({ queryKey: ["inventory"] }); qc.invalidateQueries({ queryKey: ["inv-txns"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Central Inventory" sub="Transaction-based stock. Every change is recorded — never silently overwritten." testid="inventory-title" />

      <Tabs defaultValue="stock">
        <TabsList className="border border-slate-800 bg-[#111827]">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="mt-4">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative min-w-52 flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="inventory-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search SKU or name…" className="border-slate-700 bg-[#111827] pl-9 text-slate-100 placeholder:text-slate-600" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <Checkbox data-testid="low-stock-filter" checked={lowOnly} onCheckedChange={(c) => { setLowOnly(!!c); setPage(1); }} /> Low stock only
            </label>
            {data?.buffers && (
              <p className="text-[10px] text-slate-500">Channel buffers — Amazon: {data.buffers.amazon} · Flipkart: {data.buffers.flipkart} · Store: {data.buffers.store}</p>
            )}
          </div>

          {!data?.items?.length ? <EmptyState title="No inventory found" /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
              <table className="w-full text-left text-xs" data-testid="inventory-table">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Physical</th>
                    <th className="px-4 py-3">Reserved</th>
                    <th className="px-4 py-3">Available</th>
                    <th className="px-4 py-3">Publishable (AMZ/FK/Store)</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id} data-testid={`inventory-row-${p.sku}`} className="border-b border-slate-800/50 hover:bg-slate-900/40">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-slate-200">{p.name}</p>
                        <p className="font-mono text-[10px] text-slate-500">{p.sku}</p>
                      </td>
                      <td className="px-4 py-2.5 text-slate-300">{p.stock}</td>
                      <td className="px-4 py-2.5 text-slate-400">{p.reserved}</td>
                      <td className="px-4 py-2.5">
                        <span className={p.low ? "font-bold text-amber-400" : "text-emerald-400"}>{p.available}</span>
                        {p.low && <span className="ml-1.5 rounded bg-amber-500/15 px-1 text-[9px] text-amber-400">LOW</span>}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400">
                        {p.channel_publishable.amazon} / {p.channel_publishable.flipkart} / {p.channel_publishable.store}
                      </td>
                      <td className="px-4 py-2.5">
                        <Button data-testid={`adjust-${p.sku}`} size="sm" variant="outline"
                          onClick={() => { setAdjustFor(p); setAdj({ type: "restock", qty: 10, note: "" }); }}
                          className="border-slate-700 text-slate-300 hover:bg-slate-800"><SlidersHorizontal size={11} /> Adjust</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && <Pager page={data.page} pages={data.pages} onPage={setPage} />}
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          {!txns?.items?.length ? <EmptyState title="No transactions yet" /> : (
            <div className="overflow-x-auto rounded-xl border border-slate-800 bg-[#111827]">
              <table className="w-full text-left text-xs" data-testid="inv-txn-table">
                <thead>
                  <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Time</th>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Ref</th>
                    <th className="px-4 py-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.items.map((t) => (
                    <tr key={t.id} className="border-b border-slate-800/50">
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-[10px] text-slate-500">{fmtDate(t.created_at)}</td>
                      <td className="px-4 py-2 text-slate-300">{t.product_name}</td>
                      <td className="px-4 py-2"><span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] capitalize text-slate-300">{t.type.replace(/_/g, " ")}</span></td>
                      <td className={`px-4 py-2 font-mono ${t.qty >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{t.qty > 0 ? `+${t.qty}` : t.qty}</td>
                      <td className="px-4 py-2 font-mono text-[10px] text-slate-500">{t.ref_id || "—"}</td>
                      <td className="px-4 py-2 text-slate-500">{t.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!adjustFor} onOpenChange={() => setAdjustFor(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Adjust stock — {adjustFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Type</Label>
              <Select value={adj.type} onValueChange={(v) => setAdj({ ...adj, type: v })}>
                <SelectTrigger data-testid="adjust-type-select" className="mt-1 border-slate-700 bg-slate-900 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  <SelectItem value="restock">Restock (add)</SelectItem>
                  <SelectItem value="purchase">Purchase (add)</SelectItem>
                  <SelectItem value="damage">Damage (remove)</SelectItem>
                  <SelectItem value="adjustment">Adjustment (+/−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Quantity</Label>
              <Input data-testid="adjust-qty-input" type="number" value={adj.qty} onChange={(e) => setAdj({ ...adj, qty: e.target.value })}
                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Note</Label>
              <Input data-testid="adjust-note-input" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })}
                className="mt-1 border-slate-700 bg-slate-900 text-slate-100" />
            </div>
            <Button data-testid="adjust-submit-btn" onClick={() => adjust.mutate()} disabled={adjust.isPending}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">Apply adjustment</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
